import React, { useState, useCallback } from "react";
import { MessageCircle, Loader2, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import CommentReply from "./CommentReply";
import CommentActionButtons from "./CommentActionButtons";
import { AvatarDisplay } from "./AvatarDisplay";
import {
  getCommentReplies,
  deleteComment,
} from "../../../services/commentService";
import { formatTimeAgo, formatCount } from "../../../utils/formatters";

const CommentItem = React.memo(
  ({
    comment,
    apiCall,
    onReply,
    currentUserProfileId,
    onLikeToggle,
    onShowActionMenu,
    activeCommentId,
    onDeleteComment,
    onReplyPosted,
    onReport, 
  }) => {
    const navigate = useNavigate();
    const [showReplies, setShowReplies] = useState(false);
    const [replies, setReplies] = useState([]);
    const [loadingReplies, setLoadingReplies] = useState(false);
    const [hasMoreReplies, setHasMoreReplies] = useState(false);
    const [repliesCursor, setRepliesCursor] = useState(null);
    const [isLiked, setIsLiked] = useState(comment.isLikedByUser || false);
    const [likesCount, setLikesCount] = useState(comment.stats?.likes || 0);
    const [repliesCount, setRepliesCount] = useState(
      comment.stats?.replies || 0,
    );

    const isActive = activeCommentId === comment._id;

    const getProfilePicture = () => {
      const profilePic = comment.author?.profilePicture;

      if (profilePic && typeof profilePic === "object") {
        return profilePic.thumbnail || profilePic.small || profilePic.medium;
      }

      if (typeof profilePic === "string") {
        return profilePic;
      }

      return null;
    };

    const profilePictureUrl = getProfilePicture();

    // Load replies with proper error handling
    const loadReplies = useCallback(async () => {
      if (loadingReplies) return;

      try {
        setLoadingReplies(true);
        const result = await getCommentReplies(
          apiCall,
          comment._id,
          5,
          repliesCursor,
        );

        setReplies((prev) => {
          const existingIds = new Set(prev.map((r) => r._id));
          const newReplies = result.replies.filter(
            (reply) => !existingIds.has(reply._id),
          );
          return [...prev, ...newReplies];
        });
        setHasMoreReplies(result.hasMore);
        setRepliesCursor(result.nextCursor);
        setShowReplies(true);
      } catch (error) {
        console.error("Failed to load replies:", error);
      } finally {
        setLoadingReplies(false);
      }
    }, [comment._id, apiCall, repliesCursor, loadingReplies]);

    const toggleReplies = useCallback(() => {
      if (!showReplies) {
        if (replies.length === 0) {
          loadReplies();
        }
        setShowReplies(true);
      } else {
        setShowReplies(false);
      }
    }, [showReplies, replies.length, loadReplies]);

    const handleLike = useCallback(async () => {
      const newLikedState = !isLiked;
      const previousLikesCount = likesCount;

      setIsLiked(newLikedState);
      setLikesCount((prev) => (newLikedState ? prev + 1 : prev - 1));

      try {
        await onLikeToggle(comment._id, newLikedState);
      } catch (error) {
        setIsLiked(!newLikedState);
        setLikesCount(previousLikesCount);
      }
    }, [isLiked, likesCount, comment._id, onLikeToggle]);

    const handleReplyPostedLocally = useCallback((newReply) => {
      setReplies((prev) => [newReply, ...prev]);
      setRepliesCount((prev) => prev + 1);
    }, []);

    const replaceReplyWithReal = useCallback((tempReplyId, realReply) => {
      setReplies((prev) =>
        prev.map((reply) =>
          reply._id === tempReplyId
            ? {
              ...realReply,
              stats: {
                likes: realReply.stats?.likes || 0,
                replies: realReply.stats?.replies || 0,
              },
              isLikedByUser: realReply.isLikedByUser || false,
            }
            : reply,
        ),
      );
    }, []);

    const handleReplyClick = useCallback(() => {
      onReply(comment, handleReplyPostedLocally, replaceReplyWithReal);
    }, [comment, onReply, handleReplyPostedLocally, replaceReplyWithReal]);

    const handleOptimisticDeleteReply = useCallback(
      async (replyId) => {
        const previousReplies = [...replies];
        const previousCount = repliesCount;

        try {
          // Optimistic update - remove immediately from UI
          setReplies((prev) => prev.filter((r) => r._id !== replyId));
          setRepliesCount((prev) => Math.max(0, prev - 1));
          onShowActionMenu(null);

          // Then call the parent's delete function (which handles API call + cache update)
          await onDeleteComment(replyId);
        } catch (error) {
          // Rollback on error
          setReplies(previousReplies);
          setRepliesCount(previousCount);
        }
      },
      [apiCall, onShowActionMenu, replies, repliesCount, onDeleteComment],
    );

    return (
      <div
        className={`space-y-2 px-3 rounded-lg transition-all duration-300 ${isActive
          ? "bg-white rounded-xl p-4 shadow-2xl z-20 relative scale-[1.02]"
          : ""
          }`}
      >
        {/* First Row: Profile picture and username in same line */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex-shrink-0 rounded-full overflow-hidden">
              <AvatarDisplay
                profilePictureUrl={profilePictureUrl}
                name={comment.author?.username}
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/${comment.author?.username}`)}
                className="font-semibold text-sm text-gray-900 cursor-pointer"
              >
                {comment.author?.username}
              </button>

              <span className="text-xs text-gray-400">
                {formatTimeAgo(comment.createdAt)}
              </span>
            </div>
          </div>

          {/* Three dots menu button */}
          <button
            onClick={() => onShowActionMenu(isActive ? null : comment._id)}
            className="p-1 rounded-full hover:bg-gray-200 transition-colors"
          >
            <svg width="24" height="24" viewBox="0 0 24 24">
              <line
                x1="8"
                y1="9"
                x2="16"
                y2="9"
                stroke="#6b7280"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
              <line
                x1="4"
                y1="15"
                x2="20"
                y2="15"
                stroke="#6b7280"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Second Row: Comment text */}
        <div className="flex gap-1">
          <div className="flex-shrink-0" />

          <div className="flex-1 min-w-0">
            <p className="text-sm text-black leading-relaxed break-words mb-2">
              {comment.content}
            </p>

            {/* Actions */}
            <div className="flex items-center justify-between">
              <button
                onClick={handleLike}
                className={`flex items-center gap-1.5 py-1.5 rounded-full text-xs font-medium transition-colors ${isLiked ? "text-red-500" : "text-gray-900"
                  }`}
              >
                <Heart
                  size={14}
                  className={`transition-all ${isLiked ? "fill-red-500 animate-heart-pop" : ""
                    }`}
                />
                {likesCount > 0 && <span>{formatCount(likesCount)}</span>}
              </button>

              <button
                onClick={handleReplyClick}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-full text-xs font-medium text-gray-900 hover:bg-gray-50 transition-colors"
              >
                <MessageCircle size={14} />
                Reply
              </button>
            </div>

            {/* View Replies Section */}
            {repliesCount > 0 && (
              <div className="mt-2 flex items-center">
                <div className="w-8" />
                <div className="w-6 h-px bg-blue-600" />
                <button
                  onClick={toggleReplies}
                  className="ml-2 flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                >
                  <ChevronDown
                    className={`w-3 h-3 transition-transform ${showReplies ? "rotate-180" : ""
                      }`}
                  />
                  {showReplies ? "Hide" : "View"} {formatCount(repliesCount)}{" "}
                  {repliesCount === 1 ? "reply" : "replies"}
                </button>
              </div>
            )}
          </div>
        </div >

        {/* Replies section */}
        {
          showReplies && (
            <div className="space-y-3 ml-8">
              {replies.map((reply) => (
                <div key={reply._id} className="relative">
                  <div
                    className={`transition-all duration-300 reply-fade-in ${activeCommentId && activeCommentId !== reply._id
                      ? "opacity-50 blur-[2px] pointer-events-none"
                      : ""
                      }`}
                    onClick={(e) => {
                      if (
                        activeCommentId === reply._id &&
                        !e.target.closest("button")
                      ) {
                        onShowActionMenu(null);
                      }
                    }}
                  >
                    <CommentReply
                      reply={reply}
                      onLikeToggle={onLikeToggle}
                      onShowActionMenu={onShowActionMenu}
                      activeCommentId={activeCommentId}
                    />
                  </div>

                  {/* Action buttons OUTSIDE the pointer-events-none div */}
                  {activeCommentId === reply._id && (
                    <div className="relative z-30 mt-2">
                      <CommentActionButtons
                        comment={reply}
                        currentUserProfileId={currentUserProfileId}
                        onDelete={handleOptimisticDeleteReply}
                        onReport={onReport}
                      />
                    </div>
                  )}
                </div>
              ))}

              {hasMoreReplies && (
                <button
                  onClick={loadReplies}
                  disabled={loadingReplies}
                  className="text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
                >
                  {loadingReplies ? (
                    <span className="flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Loading...
                    </span>
                  ) : (
                    "Load more replies"
                  )}
                </button>
              )}
            </div>
          )
        }
      </div >
    );
  },
);

CommentItem.displayName = "CommentItem";

export default CommentItem;
